import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatChipGrid,
  MatChipInput,
  MatChipRemove,
  MatChipRow,
} from '@angular/material/chips';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BaseDialog } from '../../../../dialog/base-dialog';

import { toSignal } from '@angular/core/rxjs-interop';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  of,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { PermissionService, triggerIfSlow } from '@ipg/ngx-frontend-toolset';
import {
  SaveContactDialogComponent,
  SaveContactDialogResult,
} from '../../../../../../app/views/settings-contacts/components/save-contact-dialog/save-contact-dialog.component';
import { ContactResponseModel } from '../../../../../models/contacts.model';
import { ContactService } from '../../../../../services/contact.service';

import { ConfirmHandler } from '../../../../dialog/dialog.model';
import { DialogService } from '../../../../dialog/dialog.service';

interface RecipientForm {
  id: FormControl<string>;
  name: FormControl<string>;
  phoneNumber: FormControl<string>;
}

type RecipientFormGroup = FormGroup<RecipientForm>;
interface NotificationSMSForm {
  message: FormControl<string | null>;
  recipients: FormArray<RecipientFormGroup>;
}

export interface NotificationSMSDialogResult {
  message: string;
  recipients: {
    id: string;
    name: string;
    phoneNumber: string;
  }[];
}

const atLeast =
  (length: number): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) {
      throw new Error('Control is not a FormArray');
    }

    return control.length >= length
      ? null
      : { atLeast: { actualLength: control.length, requiredLength: length } };
  };

@Component({
  selector: 'app-notification-sms-dialog',
  imports: [
    MatAutocompleteModule,
    MatButton,
    MatChipGrid,
    MatChipInput,
    MatChipRow,
    MatChipRemove,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatProgressSpinner,
    ReactiveFormsModule,
  ],
  templateUrl: './notification-sms-dialog.component.html',
  styleUrl: './notification-sms-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationSmsDialogComponent extends BaseDialog<
  {
    message: string;
    recipients: ContactResponseModel[];
  } & {
    onConfirm: ConfirmHandler;
    onConfirmLoadingDelay: number;
  }
> {
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _contactService = inject(ContactService);
  private readonly _dialog = inject(DialogService);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _permissionService = inject(PermissionService);
  private readonly _dialogRef =
    inject<MatDialogRef<NotificationSmsDialogComponent>>(MatDialogRef);

  protected readonly form = this._fb.group<NotificationSMSForm>({
    message: this._fb.control('', [Validators.required, Validators.maxLength(160)]),
    recipients: this._fb.array<RecipientFormGroup>(
      [],
      [Validators.required, atLeast(1)]
    ),
  });

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);
  protected readonly searching = signal(false);
  protected readonly canReadContacts = computed(() =>
    this._permissionService.hasPermission('contact:read')
  );
  protected readonly canCreateContacts = computed(() =>
    this._permissionService.hasPermission('contact:create')
  );

  private readonly _allContacts = signal<ContactResponseModel[]>([]);

  protected readonly currentRecipient = this._fb.control('');
  private readonly _searchTerm = signal('');

  protected readonly recipientList = computed(() => {
    const term = this._searchTerm().trim().toLowerCase();
    const all = this._allContacts();

    return term
      ? all.filter((contact) => contact.name.toLowerCase().includes(term))
      : all;
  });

  protected readonly recipients = toSignal(this.controls.recipients.valueChanges, {
    initialValue: [],
  });

  protected get controls() {
    return this.form.controls;
  }

  protected get recipientsArray() {
    return this.controls.recipients;
  }

  constructor() {
    super();

    this.initializeFormValues();

    if (this.canReadContacts()) {
      this.loadContacts();
    }
  }

  initializeFormValues() {
    const { message, recipients } = this.data;

    if (message && recipients) {
      this.form.patchValue({ message });

      const recipientFormGroups = recipients.map((recipient) =>
        this.createRecipientFormGroup(recipient)
      );

      recipientFormGroups.forEach((group) => this.recipientsArray.push(group));
    }
  }

  isRecipientAlreadySelected(recipient: ContactResponseModel): boolean {
    return this.recipientsArray.controls.some(
      (control) => control.value.id === recipient._id
    );
  }

  handleAddContact() {
    const dialogRef = this._dialog.open(
      SaveContactDialogComponent,
      {
        title: 'Contact toevoegen',
        onConfirm: (result?: SaveContactDialogResult) =>
          of(result).pipe(
            filter((data): data is SaveContactDialogResult => data != null),
            switchMap((data) =>
              this._contactService.create(data).pipe(
                switchMap(() =>
                  this._contactService.getAll({
                    params: {
                      q: data.name,
                      page: 1,
                      limit: 1,
                      sortBy: 'createdAt',
                      sortOrder: 'desc',
                    },
                  })
                ),
                map(({ items }) => items[0]),
                filter((contact): contact is ContactResponseModel => contact != null),
                tap((contact) => {
                  this.recipientsArray.push(this.createRecipientFormGroup(contact));
                  this.loadContacts();
                  this._cdr.markForCheck();
                }),
                catchError(() => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message:
                      'Er is iets misgegaan bij het toevoegen van een contact. Probeer het later nog eens',
                    confirmText: 'OK',
                  });

                  dialogRef.close();

                  return EMPTY;
                })
              )
            )
          ),
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  private loadContacts() {
    this._contactService
      .getAll({
        params: {
          page: 1,
          limit: 200,
          sortBy: 'name',
          sortOrder: 'asc',
        },
      })
      .pipe(
        triggerIfSlow(() => this.searching.set(true)),
        finalize(() => this.searching.set(false))
      )
      .subscribe({
        next: ({ items }) => {
          this._allContacts.set(items);
        },
      });
  }

  handleRecipientRemove(index: number) {
    this.recipientsArray.removeAt(index);
  }

  createRecipientFormGroup(recipient: ContactResponseModel): RecipientFormGroup {
    return this._fb.group({
      id: this._fb.control(recipient._id),
      name: this._fb.control(recipient.name),
      phoneNumber: this._fb.control(recipient.phoneNumber),
    });
  }

  protected onSearchInput(value: string) {
    this._searchTerm.set(value);
  }

  protected onPanelOpened() {
    this._searchTerm.set('');
  }

  handleRecipientSelected(event: MatAutocompleteSelectedEvent) {
    const chosenRecipient = event.option.value as ContactResponseModel;

    this.recipientsArray.push(this.createRecipientFormGroup(chosenRecipient));

    this.currentRecipient.setValue('', { emitEvent: false });

    event.option.deselect();
  }

  /** Used by [displayWith] to prevent the autocomplete from writing
   *  the selected object's toString() into the native input. */
  displayRecipientValue(value: ContactResponseModel | string): string {
    return typeof value === 'string' ? value : '';
  }

  override handleConfirm(): void {
    this.submitted.set(true);

    this.form.disable();
    this.currentRecipient.disable();

    const delay = 1000;

    const callback$ = this.data.onConfirm(this.form.value);

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);

        this.form.enable();
        this.currentRecipient.enable();

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(delay).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => this._dialogRef.close(true)),
        take(1)
      )
      .subscribe();
  }
}
